import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Avatar, Button, message, Space, Typography } from 'antd';
// import { TextAreaRef } from 'antd/lib/input/TextArea';
import EmojiPicker from 'emoji-picker-react';
import { MouseDownEvent } from 'emoji-picker-react/dist/config/config';
import moment from 'moment';
import { FaPlus, FaRegKeyboard } from 'react-icons/fa6';
import { ImPlay2 } from 'react-icons/im';
import { IoCloseCircle, IoCloseCircleOutline } from 'react-icons/io5';
import {
  PiChartBarFill,
  PiFileText,
  PiFolderFill,
  PiHeadphonesFill,
  PiImageFill,
  PiPaperPlaneRightFill,
  PiPlayCircleFill,
  PiSmileyLight,
} from 'react-icons/pi';
import {
  Mention,
  MentionsInput,
  MentionsInputProps,
  SuggestionDataItem,
} from 'react-mentions';
import { BottomSheet } from 'react-spring-bottom-sheet';

import AnalyticsEventsAPI from '../../../api/AnalyticsEventsAPI';
import { SocketAPI } from '../../../api/SocketAPI';
import audioWave from '../../../assets/img/audio-wave.jpg';
import {
  addNewMessage,
  setEditMessageData,
  setReplyMessageData,
  updateOrDeleteSelectedMessage,
  updateReplyState,
} from '../../../context/communityMessageReducer';
import { uploadFilesForCommunityPost } from '../../../context/communityReducer';
import showAppError from '../../../shared/error';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../shared/hooks';
import {
  checkVideoFileValidation,
  convertImage,
  getFileExtension,
} from '../../../shared/utils';
import { IMentionedUser } from '../../../types/commentTypes';
import { ICreateMessageReqBody } from '../../../types/communityMessagesTypes';
import { ISelFile, TFileTypes } from '../../../types/communityTypes';
import { PlatformIntegrationPlatformEnum } from '../../../types/integrationType';
import { ROUTES } from '../../../types/routes';
import { SOCKET_ACTION_EVENTS } from '../../../types/socketTypes';
import { Onboarding, UAMPermissions } from '../../../types/userTypes';
import CreateCommunityPostAttachment from '../../CreateCommunityPost/components/CreateCommunityPostAttachment';

const MAX_MEDIA_COUNT = 10;

type Props = {
  setFilesAndMediaCardStatus?: (arg0: { [key: string]: boolean }) => void;
  searchMentionedUser?: (search: string) => Promise<IMentionedUser[]>;
};

const CommunityInputWithEmojiPicker: React.FC<Props> = ({
  // setFilesAndMediaCardStatus,
  searchMentionedUser,
}) => {
  // store related date or params or query params data
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const { editMessageData, chatRoomDetails, replayMessageData, replyState } =
    useAppSelector((state) => state?.communityMessage);

  const userDetails = useAppSelector((state) => state?.user);
  // const { userCommunityAccess } = useAppSelector((state) => state?.community);

  // console.log(userCommunityAccess);

  // component states
  const [openEmoji, setOpenEmoji] = useState(false);
  const [openSelectMediaCard, setOpenSelectMediaCard] = useState(false);
  const [selFiles, setSelFiles] = useState<ISelFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [emptySearchResults, setEmptySearchResults] = useState<string[]>([]);

  // componenet refs
  // const textareaRef = useRef<TextAreaRef | null>(null);

  const mentionsRef = useRef<React.Component<MentionsInputProps>>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const suggestionContainerRef = useRef<HTMLDivElement>(null);

  const closeModal = () => {
    setOpenEmoji(false);
  };

  const canUploadMedia = useMemo(() => {
    if (userDetails?.type === Onboarding.FAN_COMPLETED) {
      const messageShareSattings =
        chatRoomDetails?.channel?.featureSettings?.messages
          ?.sharingControlSettings;
      if (messageShareSattings) {
        return Object.values(messageShareSattings).some((each) => each);
      }
      return false;
    }
    return true;
  }, [chatRoomDetails?.channel, userDetails?.type]);

  const addedIcon = useMemo(
    () => (
      <div className="addon">
        <button
          className="addon_button"
          disabled={loading}
          onClick={() => {
            if (openEmoji) {
              inputRef.current?.focus();
            }
            setOpenEmoji((prev) => !prev);
          }}>
          {openEmoji ? <FaRegKeyboard /> : <PiSmileyLight />}
        </button>
      </div>
    ),
    [loading, openEmoji],
  );

  const setImgFile = (imageFile: File, url: string) => {
    const ext = getFileExtension(imageFile.name)?.[1].toLowerCase() || '';
    const name = imageFile.name.substring(
      0,
      imageFile.name.length - ext.length - 1,
    );
    setSelFiles((prev) => {
      const oldState = prev?.length ? prev : [];
      return [
        ...oldState,
        {
          file: imageFile,
          name,
          ext,
          type: 'image',
          url,
        },
      ];
    });
  };

  const beforeUpload = async (fileData: File, fileType: TFileTypes | 'doc') => {
    let clickedFileType = fileType;
    let isValid = true;
    let url: string | null = null;

    // when image
    if (clickedFileType === 'image') {
      // const isValidImageExtension =
      //     validImageExtensions.indexOf(file.type) !== -1;
      const type = getFileExtension(fileData.name)?.[1].toLowerCase();
      const isImage =
        type === 'jpg' ||
        type === 'png' ||
        type === 'gif' ||
        type === 'webp' ||
        type === 'tiff' ||
        type === 'jpeg' ||
        type === 'heic' ||
        type === 'svg';
      if (!isImage) {
        isValid = false;
        message.error('You can only put a valid image here!');
        return false;
      }

      const isImageLt2M = fileData.size / 1024 / 1024 < 1024;

      if (!isImageLt2M) {
        isValid = false;
        message.error('Image must smaller than 1024MB!');
        return false;
      }

      convertImage(type, fileData, setImgFile);
      return;
    }

    // when video
    if (clickedFileType === 'video') {
      const isVideo = checkVideoFileValidation(fileData);
      if (!isVideo) {
        isValid = false;
        message.error('You can only upload valid video file!');
      }

      if (fileData?.size > 5 * 1024 * 1024 * 1024) {
        message.error('File limit is upto 5GB');
        return false;
      }

      // create video preview
      url = URL.createObjectURL(fileData);
      // videoParentRef?.current?.load();
    }

    // when audio
    if (clickedFileType === 'audio') {
      const fileExtension = getFileExtension(fileData.name)?.[1];
      if (fileExtension === undefined || fileExtension === null) {
        isValid = false;
        message.error('Sorry, something went wrong. Try another audio file.');
      } else {
        const type = fileExtension.toLowerCase();
        const isAudio =
          type === 'mp3' ||
          type === 'm4a' ||
          type === 'aac' ||
          type === 'm3u' ||
          type === 'oga' ||
          type === 'wav';
        if (!isAudio) {
          isValid = false;
          message.error('You can only upload audio file!');
        }
        url = URL.createObjectURL(fileData);
      }
    }

    if (clickedFileType === 'doc') {
      isValid = true;
      url = URL.createObjectURL(fileData);
    }

    if (isValid && url && typeof url === 'string') {
      // message.success("File added successfully.");
      const ext = getFileExtension(fileData.name)?.[1].toLowerCase() || '';
      const name = fileData.name.substring(
        0,
        fileData.name.length - ext.length - 1,
      );
      setSelFiles((prev) => {
        const oldState = prev?.length ? prev : [];
        return [
          ...oldState,
          {
            file: fileData,
            name,
            ext,
            type: clickedFileType,
            url: typeof url === 'string' ? url : '',
          } as ISelFile,
        ];
      });
    } else {
      message.error('File upload failed.');
    }
  };

  const onSelect = async (type: TFileTypes | 'doc') => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('multiple', true as unknown as string);
    if (type === 'image')
      input.setAttribute(
        'accept',
        '.jpg, .jpeg, .png, .svg, .gif, .bmp, .webp, .tiff, .heic',
      );
    else if (type === 'video')
      input.setAttribute(
        'accept',
        '.video/mp4, .mp4, .video/webm, .webm, .video/quicktime, .quicktime, .video/mov, .mov, .video/mkv, .mkv',
      );
    else if (type === 'audio')
      input.setAttribute('accept', '.mp3, .m4a, .aac, .m3u, .oga, .wav');
    input.click();
    input.addEventListener('change', (e: any) => {
      let evt: React.ChangeEvent<HTMLInputElement> = e as any;
      if (!evt.target?.files) return;
      const filesArray = Array.from(e.target.files) as File[];

      if (filesArray.length > MAX_MEDIA_COUNT - (selFiles?.length || 0)) {
        e.preventDefault();
        alert(`Cannot upload files more than ${MAX_MEDIA_COUNT}`);
        return;
      }

      // Check for video files > 50MB
      if (type === 'video') {
        const oversizedVideo = filesArray.find(
          (file: File) => file.size > 50 * 1024 * 1024,
        );
        if (oversizedVideo) {
          alert('Cannot upload video files larger than 50MB.');
          return;
        }
      }

      filesArray.map((eachFile) => {
        beforeUpload(eachFile as File, type);
      });
      setOpenSelectMediaCard(false);
    });
  };

  const postContentOptions: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    backgroundColor?: string;
  }[] = useMemo(() => {
    if (!canUploadMedia) return [];
    const sharingControlSettings =
      chatRoomDetails?.channel?.featureSettings?.messages
        ?.sharingControlSettings;
    const isCreator =
      userDetails?.type === Onboarding.CREATOR_COMPLETED ||
      (userDetails?.type === Onboarding.CREATOR_RESTRICTED &&
        userDetails?.userAccessPermissions?.includes(
          UAMPermissions.COMMUNITY_ACCESS,
        ));
    if (sharingControlSettings) {
      const iconSize = 26;
      const items = [
        sharingControlSettings?.allowFileSharing || isCreator
          ? {
              icon: <PiFolderFill size={iconSize} fill="#000" />,
              label: 'File',
              backgroundColor: '#F2F2F5',
              onClick: () => {
                onSelect('doc');
              },
            }
          : null,
        sharingControlSettings?.allowPhotoSharing || isCreator
          ? {
              icon: <PiImageFill size={iconSize} fill="#0091FF" />,
              backgroundColor: '#EDF6FF',
              label: 'Photo',
              onClick: () => {
                onSelect('image');
              },
            }
          : null,
        sharingControlSettings?.allowVideoSharing || isCreator
          ? {
              icon: <PiPlayCircleFill size={iconSize} fill="#E5484D" />,
              label: 'Video',
              backgroundColor: '#FFEFEF',
              onClick: () => {
                onSelect('video');
              },
            }
          : null,
        sharingControlSettings?.allowAudioSharing || isCreator
          ? {
              icon: <PiHeadphonesFill size={iconSize} fill="#8E4EC6" />,
              label: 'Audio',
              backgroundColor: '#F9F1FE',
              onClick: () => {
                onSelect('audio');
              },
            }
          : null,
        sharingControlSettings?.allowPollSharing || isCreator
          ? {
              icon: <PiChartBarFill size={iconSize} fill="#30A46C" />,
              label: 'Polls',
              backgroundColor: '#E9F9EE',
              onClick: () => {
                const query = new URLSearchParams(location?.search);
                query.append('roomId', chatRoomDetails?._id as string);
                navigate(
                  ROUTES.COMMUNITY_CREATE_POLL,
                  {},
                  {
                    search: query.toString(),
                    replace: false,
                  },
                );
              },
            }
          : null,
      ];
      return items.filter((each) => each !== null) as {
        icon: React.ReactNode;
        label: string;
        onClick: () => void;
        backgroundColor?: string;
      }[];
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canUploadMedia,
    chatRoomDetails?._id,
    chatRoomDetails?.channel?.featureSettings?.messages?.sharingControlSettings,
    navigate,
    userDetails?.type,
  ]);

  const onSelectEmoji = useCallback((emoji: string) => {
    if (!inputRef.current) return;

    const textarea = inputRef.current;

    if (!textarea) return;

    let selectionStart = textarea?.selectionStart;
    let selectionEnd = textarea?.selectionEnd;

    if (selectionStart === null || selectionEnd === null) return;

    if (
      selectionStart === 0 &&
      selectionEnd === 0 &&
      textarea?.textLength > 0
    ) {
      selectionStart = textarea?.textLength;
      selectionEnd = textarea?.textLength;
    }
    setText(
      (prev) =>
        prev.slice(0, selectionStart) + emoji + prev.slice(selectionEnd),
    );
    const newCursor = selectionStart + emoji.length;
    setCursorPosition(newCursor);
    setTimeout(() => textarea.setSelectionRange(newCursor, newCursor), 10);
    textarea.focus();
  }, []);

  const addMessage = useCallback(async () => {
    if (!chatRoomDetails?._id) return;
    if (!text?.trim() && !selFiles?.length) return;
    setLoading(true);
    let assets: string[] = [];
    if (selFiles?.length) {
      const resp = await dispatch(
        uploadFilesForCommunityPost(
          selFiles.map((each) => each?.file) as File[],
        ),
      );
      if (resp && resp?.length > 0) {
        assets = resp.map((each) => each._id);
      }
    }
    const reqBody: ICreateMessageReqBody = {
      room: chatRoomDetails?._id,
      message: text || ' ',
      assets,
    };
    if (replayMessageData?._id) {
      reqBody.threadRoot = replayMessageData?._id;
    }
    SocketAPI.emit(
      SOCKET_ACTION_EVENTS.ROOM_MESSAGE_CREATE,
      reqBody,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (resp: any) => {
        if (resp?.status === 'success') {
          if (replayMessageData?._id) {
            dispatch(
              updateReplyState({
                replies: [...replyState?.replies, resp.result],
              }),
            );
            const currentThreadMetadata =
              replayMessageData?.threadRootMetadata || {
                totalReplies: 0,
                lastRepliedAt: moment().toISOString(),
                lastReply: resp.result?._id,
              };
            const currentReplyCount = currentThreadMetadata?.totalReplies || 0;
            const newReplyCount = currentReplyCount + 1;
            dispatch(
              setReplyMessageData({
                ...replayMessageData,
                threadRootMetadata: {
                  ...currentThreadMetadata,
                  totalReplies: newReplyCount,
                },
              }),
            );
            dispatch(
              updateOrDeleteSelectedMessage(
                {
                  ...replayMessageData,
                  threadRootMetadata: {
                    ...currentThreadMetadata,
                    totalReplies: newReplyCount,
                  },
                },
                false,
                () => {},
              ),
            );
          } else {
            dispatch(addNewMessage(resp.result));
          }
          setTimeout(() => {
            const messageCard = document.getElementById(resp.result?._id);
            if (messageCard) {
              messageCard.scrollIntoView({ behavior: 'smooth' });
            }
          }, 700);
          setText('');
          setSelFiles([]);
          // message.success('Message update succesfully');
          setTimeout(() => {
            // if (scrollContainerRef.current) {
            //   scrollContainerRef.current.scrollTop = 0;
            // }
          }, 500);
          try {
            AnalyticsEventsAPI.trackCommunityEvents('MESSAGE_DONE', {
              user_id: userDetails.id,
              onboarding: userDetails.type,
              name: userDetails.name,
              email: userDetails.email,
              phone_number: userDetails.phone,
              message_id: resp.result._id,
              room_id: chatRoomDetails?._id,
              platform: PlatformIntegrationPlatformEnum.MOBILE,
              host: userDetails.host || window.location.host,
            });
          } catch (error: any) {
            console.log('Error in tracking message done events:', error);
          }
        } else if (resp?.status === 'error') {
          message.error(resp?.message);
        }
      },
    );
    setLoading(false);
  }, [
    chatRoomDetails?._id,
    dispatch,
    replayMessageData,
    replyState?.replies,
    selFiles,
    text,
    userDetails.email,
    userDetails.host,
    userDetails.id,
    userDetails.name,
    userDetails.phone,
    userDetails.type,
  ]);

  const handleCloseEditMessage = useCallback(() => {
    setText('');
    dispatch(setEditMessageData(null));
  }, [dispatch]);

  const editMessage = useCallback(() => {
    if (!editMessageData?._id || !chatRoomDetails?._id) return;
    setLoading(true);
    SocketAPI.emit(
      SOCKET_ACTION_EVENTS.ROOM_MESSAGE_EDIT,
      {
        room: chatRoomDetails?._id,
        messageId: editMessageData?._id,
        message: text,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (resp: any) => {
        if (resp?.status === 'success') {
          if (replayMessageData?._id) {
            const newReplyState = replyState?.replies?.map((each) => {
              if (each?._id === replayMessageData?._id) {
                return resp.result;
              }
              return each;
            });
            dispatch(updateReplyState({ replies: newReplyState }));
            message.success('Edited message successfully');
          } else {
            dispatch(
              updateOrDeleteSelectedMessage(resp.result, false, () =>
                message.success('Edited message successfully'),
              ),
            );
          }
          handleCloseEditMessage();
        } else if (resp?.status === 'error') {
          message.error(resp?.message || 'Failed to edit message');
        }
      },
    );
    setLoading(false);
  }, [
    chatRoomDetails?._id,
    dispatch,
    editMessageData?._id,
    handleCloseEditMessage,
    replayMessageData?._id,
    replyState?.replies,
    text,
  ]);

  const displayMedia = (fileData: ISelFile) => {
    if (fileData?.type === 'image') {
      return (
        <div className="seleted-media_wrapper_media_item">
          <img src={fileData?.url} />
        </div>
      );
    }
    if (fileData?.type === 'video') {
      return (
        <div className="seleted-media_wrapper_media_item video">
          <ImPlay2 />
        </div>
      );
    }
    if (fileData?.type === 'audio') {
      return (
        <div className="seleted-media_wrapper_media_item">
          <img src={audioWave} />
        </div>
      );
    }
    // if (fileData?.type === 'doc') {
    return (
      <div className="seleted-media_wrapper_media_file">
        <div className="seleted-media_wrapper_media_file_icon">
          <PiFileText />
        </div>
        <div className="seleted-media_wrapper_media_file_name">
          <p>{fileData?.name}</p>
          {fileData?.ext ? <span>{fileData?.ext}</span> : null}
        </div>
      </div>
    );
    // }
    // return null;
  };

  const handleSend = useCallback(() => {
    const textValue = text.trim();

    // if (!textValue) return;

    if (!textValue && selFiles?.length === 0) {
      message.warning('Type a message or upload a file to send');
      return;
    }

    const action = editMessageData?._id ? editMessage : addMessage;

    action();
  }, [addMessage, editMessage, editMessageData?._id, selFiles, text]);

  const onMentionSearch = async (
    searchText: string,
    callback: (mentions: IMentionedUser[]) => void,
  ) => {
    if (!searchMentionedUser) {
      callback([]);
      return;
    }

    if (searchText.length === 0) {
      callback([]);
      return;
    }

    if (emptySearchResults.find((item) => searchText.startsWith(item))) {
      callback([]);
      // eslint-disable-next-line no-useless-return
      return;
    }

    try {
      const result = await searchMentionedUser(searchText);

      if (result.length === 0) {
        setEmptySearchResults((prev) => [...prev, searchText]);
      }

      callback(
        result.map((user) => ({
          id: user.id,
          display: user.display,
          profilePicUrl: user.profilePicUrl,
        })),
      );
    } catch (error) {
      showAppError(error);
    }
  };

  const handleOnEmojiClick: MouseDownEvent = (event) => {
    onSelectEmoji(event?.emoji);
  };

  const handleAddFile = () => {
    // if (openSelectMediaCard) {
    //   setSelFiles([]);
    // }
    setOpenSelectMediaCard((prev) => !prev);
  };

  useEffect(() => {
    if (editMessageData) {
      setText(editMessageData?.message);
    } else {
      setText('');
    }
  }, [editMessageData]);

  // useEffect(() => {
  //   if (setFilesAndMediaCardStatus) {
  //     setFilesAndMediaCardStatus({
  //       files: selFiles?.length > 0,
  //       mediaCard: openSelectMediaCard,
  //     });
  //   }
  // }, [setFilesAndMediaCardStatus, selFiles, openSelectMediaCard]);

  useEffect(() => {
    if (openEmoji && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
      inputRef.current.focus();
    }
  }, [openEmoji, cursorPosition]);

  const isHideMessageInputObject = useMemo(() => {
    if (
      chatRoomDetails?.roomType === 'dm' &&
      (chatRoomDetails?.selfStopReceivingMessages ||
        chatRoomDetails?.otherStopReceivingMessages)
    ) {
      return {
        isHideMessageInput: true,
        message: chatRoomDetails?.selfStopReceivingMessages
          ? 'You stopped receiving messages from this person'
          : "You can't send messages to this person",
      };
    }
    if (
      chatRoomDetails?.roomType === 'channel' &&
      chatRoomDetails?.channel?.featureSettings?.messages
        ?.allowEveryoneToMessage === false &&
      chatRoomDetails?.communityOwner !== userDetails?.id &&
      !userDetails?.userAccessPermissions?.includes(
        UAMPermissions.COMMUNITY_ACCESS,
      )
    ) {
      return {
        isHideMessageInput: true,
        message: "You can't send messages to this group",
      };
    }

    return { isHideMessageInput: false, message: '' };
  }, [
    chatRoomDetails?.channel?.featureSettings?.messages?.allowEveryoneToMessage,
    chatRoomDetails?.communityOwner,
    chatRoomDetails?.otherStopReceivingMessages,
    chatRoomDetails?.roomType,
    chatRoomDetails?.selfStopReceivingMessages,
    userDetails?.id,
    userDetails?.userAccessPermissions,
  ]);

  if (isHideMessageInputObject?.isHideMessageInput) {
    return (
      <div className="chat-input_wrapper">
        <p className="chat-input_wrapper_text">
          {isHideMessageInputObject?.message}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="chat-input_wrapper">
        {chatRoomDetails?.roomType === 'channel' &&
        !chatRoomDetails?.channel?.featureSettings?.messages
          ?.allowEveryoneToMessage ? (
          <p className="chat-input_wrapper_text" style={{ marginBottom: 12 }}>
            Customers are not allowed to message in this group
          </p>
        ) : null}
        <div className="chat-input_wrapper_">
          {canUploadMedia || editMessageData?._id ? (
            <div
              className={`chat-input_wrapper__plus ${
                openSelectMediaCard ? 'active' : ''
              }`}>
              <button
                onClick={
                  editMessageData?._id ? handleCloseEditMessage : handleAddFile
                }>
                {editMessageData?._id ? <IoCloseCircleOutline /> : <FaPlus />}
              </button>
            </div>
          ) : null}
          <div className="chat-input_wrapper__input">
            <MentionsInput
              id={`input${chatRoomDetails?._id ?? ''}`}
              className="chat-input__box"
              style={{ width: '100%' }}
              disabled={loading}
              placeholder={'Type a message'}
              singleLine={false}
              allowSpaceInQuery
              ref={mentionsRef}
              inputRef={inputRef}
              suggestionsPortalHost={
                suggestionContainerRef.current ?? undefined
              }
              allowSuggestionsAboveCursor
              forceSuggestionsAboveCursor
              value={text}
              onChange={(_: any, value: string) => {
                setText(value);
                if (inputRef.current) {
                  setCursorPosition(inputRef.current.selectionStart);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}>
              <Mention
                trigger="@"
                data={onMentionSearch}
                appendSpaceOnAdd
                markup="{{__id__#__display__}}"
                displayTransform={(_, display) => `@${display}`}
                renderSuggestion={(
                  suggestion: SuggestionDataItem,
                  _: string,
                  highlightedDisplay: React.ReactNode,
                ) => (
                  <div className="mention-suggestion">
                    <Space size={8}>
                      <Avatar
                        src={(suggestion as IMentionedUser).profilePicUrl}
                        alt="image"
                        size={32}
                        style={{ flexShrink: 0 }}
                      />
                      <Typography.Text className="mention-suggestion__name">
                        {highlightedDisplay}
                      </Typography.Text>
                    </Space>
                  </div>
                )}
              />
            </MentionsInput>
            {addedIcon}
          </div>
          <div className="chat-input_wrapper__send">
            <button
              disabled={loading}
              onClick={editMessageData?._id ? editMessage : addMessage}>
              <PiPaperPlaneRightFill />
            </button>
          </div>
        </div>
        {openSelectMediaCard ? (
          <div className="chat-media-wrapper">
            <CreateCommunityPostAttachment
              noBorder
              options={postContentOptions}
            />
          </div>
        ) : null}
        {selFiles?.length > 0 ? (
          <div className="seleted-media_wrapper">
            {selFiles.map((each, index) => (
              <div className="seleted-media_wrapper_media">
                {displayMedia(each)}
                <div className="seleted-media_wrapper_media_close">
                  <Button
                    onClick={() => {
                      setSelFiles((prev) => {
                        const newFileList = [...prev];
                        newFileList.splice(index, 1);
                        return newFileList;
                      });
                    }}>
                    <IoCloseCircle size={24} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
      <BottomSheet
        open={openEmoji}
        onDismiss={() => closeModal()}
        style={{ zIndex: 0 }}
        snapPoints={({ maxHeight }) => [maxHeight * 0.6]}
        defaultSnap={({ snapPoints }) => snapPoints[0]}
        blocking={false}
        skipInitialTransition
        expandOnContentDrag={false}
        className="emoji-picker-bottom-sheet">
        <>
          {openEmoji ? (
            <div
              className="chat-input_wrapper"
              style={{ position: 'relative' }}>
              <div className="chat-input_wrapper_">
                <div className="chat-input_wrapper__input">
                  <MentionsInput
                    id={`input-with-emoji-in-emoji-picker${
                      chatRoomDetails?._id ?? ''
                    }`}
                    className="chat-input__box"
                    style={{ width: '100%' }}
                    disabled={loading}
                    placeholder={'Type a message'}
                    singleLine={false}
                    allowSpaceInQuery
                    ref={mentionsRef}
                    inputRef={inputRef}
                    suggestionsPortalHost={
                      suggestionContainerRef.current ?? undefined
                    }
                    allowSuggestionsAboveCursor
                    forceSuggestionsAboveCursor
                    value={text}
                    onChange={(_: any, value: string) => {
                      setText(value);
                      if (inputRef.current) {
                        setCursorPosition(inputRef.current.selectionStart);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}>
                    <Mention
                      trigger="@"
                      data={() => {}}
                      appendSpaceOnAdd
                      markup="{{__id__#__display__}}"
                      displayTransform={(_, display) => `@${display}`}
                      renderSuggestion={(
                        suggestion: SuggestionDataItem,
                        _: string,
                        highlightedDisplay: React.ReactNode,
                      ) => (
                        <div
                          className="mention-suggestion"
                          style={{ zIndex: 100000 }}>
                          <Space size={8}>
                            <Avatar
                              src={(suggestion as IMentionedUser).profilePicUrl}
                              alt="image"
                              size={32}
                              style={{ flexShrink: 0 }}
                            />
                            <Typography.Text className="mention-suggestion__name">
                              {highlightedDisplay}
                            </Typography.Text>
                          </Space>
                        </div>
                      )}
                    />
                  </MentionsInput>
                  {addedIcon}
                </div>
                <div className="chat-input_wrapper__send">
                  <button
                    disabled={loading}
                    onClick={editMessageData?._id ? editMessage : addMessage}>
                    <PiPaperPlaneRightFill />
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </>
        <EmojiPicker
          className="emoji_container_wrapper"
          onEmojiClick={handleOnEmojiClick}
          autoFocusSearch={false}
          lazyLoadEmojis={true}
          skinTonesDisabled={true}
          searchPlaceholder="Search emoji..."
          width="100%"
          height={400}
          previewConfig={{
            showPreview: false,
          }}
          suggestedEmojisMode={undefined}
          theme={undefined}
        />
      </BottomSheet>
    </>
  );
};

export default memo(CommunityInputWithEmojiPicker);
